// frontend/src/pages/ListaPixEmitidos.jsx
import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Search, 
  Filter, 
  TrendingUp, 
  CheckCircle, 
  Clock, 
  XCircle,
  Eye,
  X,
  Copy,
  Download,
  AlertCircle,
  Calendar,
  DollarSign,
  User,
  FileText,
  QrCode,
  ChevronDown,
  ChevronUp,
  Send,
} from 'lucide-react';
import axios from 'axios';

const ListaPixEmitidos = () => {
  const [pixList, setPixList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // ⭐ ATUALIZADO: Filtros com ordenação padrão por mais recentes
  const [filtros, setFiltros] = useState({
    search: '',
    status: '',
    dataInicio: '',
    dataFim: '',
    page: 1,
    limit: 50,
    orderBy: 'created_at', // ⭐ NOVO: Campo de ordenação
    orderDirection: 'DESC' // ⭐ NOVO: Direção (DESC = mais recentes primeiro)
  });
  
  const [pagination, setPagination] = useState({});
  const [estatisticas, setEstatisticas] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [pixSelecionado, setPixSelecionado] = useState(null);
  const [modalDetalhes, setModalDetalhes] = useState(false);
  const [loadingDetalhes, setLoadingDetalhes] = useState(false);
  const [reenviando, setReenviando] = useState(false);
  const [reenvioStatus, setReenvioStatus] = useState(null);
  const [modalReenviar, setModalReenviar] = useState(false);
  const [pixParaReenviar, setPixParaReenviar] = useState(null);
  const [dadosReenvio, setDadosReenvio] = useState({
    celular: '',
    email: ''
  });
  const [copiado, setCopiado] = useState(null);

  // Carregar lista de PIX
  const carregarPixList = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      Object.keys(filtros).forEach(key => {
        if (filtros[key]) {
          params.append(key, filtros[key]);
        }
      });

      const response = await axios.get(`/api/lista-pix?${params.toString()}`);
      
      if (response.data.success) {
        setPixList(response.data.data);
        setPagination(response.data.pagination);
        setEstatisticas(response.data.estatisticas);
      } else {
        setError('Erro ao carregar lista de PIX');
      }
    } catch (err) {
      console.error('Erro ao carregar PIX:', err);
      setError(err.response?.data?.message || 'Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Carregar detalhes do PIX
  const carregarDetalhes = async (id) => {
    try {
      setLoadingDetalhes(true);
      const response = await axios.get(`/api/lista-pix/${id}`);
      
      if (response.data.success) {
        setPixSelecionado(response.data.data);
        setModalDetalhes(true);
      } else {
        alert('Erro ao carregar detalhes do PIX');
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err);
      alert(err.response?.data?.message || 'Erro ao carregar detalhes do PIX');
    } finally {
      setLoadingDetalhes(false);
    }
  };

  // ⭐ NOVO: Função para fechar modal e atualizar lista automaticamente
  const fecharModalEAtualizar = () => {
    setModalDetalhes(false);
    setPixSelecionado(null);
    // Atualiza a lista automaticamente ao fechar o modal
    carregarPixList();
  };

  // ⭐ ATUALIZADO: Carregar ao montar e quando filtros mudarem
  useEffect(() => {
    carregarPixList();
  }, [filtros.page, filtros.status, filtros.orderBy, filtros.orderDirection]);

  // Aplicar filtros
  const aplicarFiltros = () => {
    setFiltros(prev => ({ ...prev, page: 1 }));
    carregarPixList();
  };

  // ⭐ NOVO: Limpar filtros (mantém ordenação)
  const limparFiltros = () => {
    setFiltros({
      search: '',
      status: '',
      dataInicio: '',
      dataFim: '',
      page: 1,
      limit: 50,
      orderBy: 'created_at',
      orderDirection: 'DESC'
    });
  };

  // ⭐ NOVO: Alternar ordenação
  const alternarOrdenacao = (campo) => {
    setFiltros(prev => ({
      ...prev,
      orderBy: campo,
      orderDirection: prev.orderBy === campo && prev.orderDirection === 'DESC' ? 'ASC' : 'DESC',
      page: 1
    }));
  };

  // Copiar para área de transferência
  const copiarTexto = (texto, label) => {
    navigator.clipboard.writeText(texto)
      .then(() => {
        setCopiado(label);
        setTimeout(() => setCopiado(null), 2000);
      })
      .catch(() => {
        setCopiado('erro');
        setTimeout(() => setCopiado(null), 2000);
      });
  };

  // Formatar valor em Real
  const formatarValor = (valor) => {
    if (!valor) return 'R$ 0,00';
    return `R$ ${parseFloat(valor).toFixed(2).replace('.', ',')}`;
  };

  // Formatar data
  const formatarData = (data) => {
    if (!data) return '-';
    
    try {
      const date = new Date(data);
      if (isNaN(date.getTime())) return '-';
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      return '-';
    }
  };

  // Formatar data e hora
  const formatarDataHora = (data) => {
    if (!data) return '-';
    
    try {
      const date = new Date(data);
      if (isNaN(date.getTime())) return '-';
      
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '-';
    }
  };

  // Formatar hora
  const formatarHora = (data) => {
    if (!data) return '';
    
    try {
      const date = new Date(data);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleTimeString('pt-BR', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      return '';
    }
  };

  // ⭐ NOVO: Calcular horas restantes para expiração do PIX
  const calcularHorasRestantes = (pix) => {
    if (!pix.pix_expires_at) return null;
    
    try {
      const agora = new Date();
      const expiracao = new Date(pix.pix_expires_at);
      
      if (isNaN(expiracao.getTime())) return null;
      
      const diferencaMs = expiracao - agora;
      const horasRestantes = diferencaMs / (1000 * 60 * 60);
      
      return horasRestantes;
    } catch (error) {
      console.error('Erro ao calcular horas restantes:', error);
      return null;
    }
  };

  // ⭐ NOVO: Verificar se pode reenviar o PIX
  const podeReenviarPix = (pix) => {
    if (pix.status !== 'pending') return false;
    if (!pix.celular || pix.celular.trim() === '') return false;
    if (!pix.pix_qr_code || pix.pix_qr_code.trim() === '') return false;
    
    const horasRestantes = calcularHorasRestantes(pix);
    
    // Se não tem data de expiração definida, considera válido para reenvio
    if (horasRestantes === null) return true;
    
    return horasRestantes > 6 && horasRestantes > 0;
  };

  // ⭐ NOVO: Formatar tempo restante para exibição
  const formatarTempoRestante = (pix) => {
    const horasRestantes = calcularHorasRestantes(pix);
    if (horasRestantes === null) return 'Sem data de expiração';
    
    if (horasRestantes <= 0) return 'Expirado';
    
    if (horasRestantes < 1) {
      const minutos = Math.floor(horasRestantes * 60);
      return `${minutos} min`;
    } else if (horasRestantes < 24) {
      return `${horasRestantes.toFixed(1)} h`;
    } else {
      const dias = (horasRestantes / 24).toFixed(1);
      return `${dias} dias`;
    }
  };

  // ⭐ NOVO: Função para abrir modal de reenvio de PIX
  const abrirModalReenviar = (pix) => {
    setPixParaReenviar(pix);
    
    // Preencher com dados do PIX se existirem
    setDadosReenvio({
      celular: pix.celular || '',
      email: pix.email || ''
    });
    
    setModalReenviar(true);
  };

  // ⭐ NOVO: Função para reenviar PIX (chamada pelo modal)
  const executarReenvioPix = async (pixId, dados) => {
    try {
      setReenviando(true);
      setReenvioStatus(null);
      
      console.log(`🔄 Reenviando PIX ID: ${pixId} com dados:`, dados);
      
      const response = await axios.post(`/api/pix/reenviar/${pixId}`, {
        celular: dados.celular,
        email: dados.email || null
      });
      
      if (response.data.success) {
        setReenvioStatus({
          type: 'success',
          message: `PIX reenviado para ${dados.nome_cliente}`,
          detalhes: response.data.message
        });
        
        // Limpar status após 5 segundos
        setTimeout(() => {
          setReenvioStatus(null);
        }, 5000);
        
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || 'Erro ao reenviar PIX');
      }
    } catch (error) {
      console.error('❌ Erro ao reenviar PIX:', error);
      
      const mensagemErro = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Erro ao reenviar PIX';
      
      const detalhesErro = error.response?.data?.detalhes || 
                          error.response?.data?.recomendacao || 
                          'Tente novamente mais tarde';
      
      setReenvioStatus({
        type: 'error',
        message: mensagemErro,
        detalhes: detalhesErro
      });
      
      // Limpar status após 8 segundos para erros
      setTimeout(() => {
        setReenvioStatus(null);
      }, 8000);
      
      return { success: false, error: mensagemErro };
    } finally {
      setReenviando(false);
    }
  };

  // ⭐ NOVO: Componente de cabeçalho de coluna ordenável
  const HeaderOrdenavel = ({ campo, children }) => {
    const isActive = filtros.orderBy === campo;
    const Icon = isActive && filtros.orderDirection === 'DESC' ? ChevronDown : ChevronUp;

    return (
      <th 
        className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={() => alternarOrdenacao(campo)}
      >
        <div className="flex items-center gap-2">
          {children}
          {isActive && <Icon className="w-4 h-4" />}
        </div>
      </th>
    );
  };

  // Configuração de status
  const statusConfig = {
    paid: {
      label: 'Pago',
      color: 'bg-green-100 text-green-800 border-green-200',
      icon: CheckCircle,
      iconColor: 'text-green-600'
    },
    pending: {
      label: 'Pendente',
      color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: Clock,
      iconColor: 'text-yellow-600'
    },
    expired: {
      label: 'Expirado',
      color: 'bg-red-100 text-red-800 border-red-200',
      icon: XCircle,
      iconColor: 'text-red-600'
    },
    canceled: {
      label: 'Cancelado',
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: XCircle,
      iconColor: 'text-gray-600'
    }
  };

  // Renderizar status badge
  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <Icon className={`w-3.5 h-3.5 ${config.iconColor}`} />
        {config.label}
      </span>
    );
  };

  // Modal de Detalhes
  const modalDetalhesNode = modalDetalhes && pixSelecionado ? (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Detalhes do PIX</h3>
              <p className="text-sm text-gray-500">ID: {pixSelecionado.pedido_id}</p>
            </div>
            <button
              onClick={fecharModalEAtualizar}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6 space-y-6">
            {/* Status e Valores */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
                  <DollarSign className="w-4 h-4" />
                  Valor do PIX
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatarValor(pixSelecionado.valor)}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-gray-600 text-sm mb-2">Status</div>
                <StatusBadge status={pixSelecionado.status} />
              </div>

              {pixSelecionado.valor_pago && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="text-green-700 text-sm mb-2">Valor Pago</div>
                  <div className="text-2xl font-bold text-green-900">
                    {formatarValor(pixSelecionado.valor_pago)}
                  </div>
                </div>
              )}
            </div>

            {/* Informações do Cliente */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center gap-2 text-blue-900 font-semibold mb-3">
                <User className="w-5 h-5" />
                Informações do Cliente
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <span className="text-sm text-blue-700">Nome:</span>
                  <div className="font-medium text-blue-900">{pixSelecionado.nome_cliente}</div>
                </div>
                <div>
                  <span className="text-sm text-blue-700">Código:</span>
                  <div className="font-medium text-blue-900">{pixSelecionado.codigo_cliente}</div>
                </div>
                <div>
                  <span className="text-sm text-blue-700">Ordem:</span>
                  <div className="font-medium text-blue-900">{pixSelecionado.ordem_cliente}</div>
                </div>
              </div>
            </div>

            {/* Datas */}
            <div>
              <div className="flex items-center gap-2 text-gray-900 font-semibold mb-3">
                <Calendar className="w-5 h-5" />
                Datas
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm text-gray-600">Emissão:</span>
                  <div className="font-medium text-gray-900">
                    {formatarDataHora(pixSelecionado.created_at)}
                  </div>
                </div>

                {pixSelecionado.data_vencimento_original && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-sm text-gray-600">Vencimento Original:</span>
                    <div className="font-medium text-gray-900">
                      {formatarData(pixSelecionado.data_vencimento_original)}
                    </div>
                  </div>
                )}

                {pixSelecionado.pix_expires_at && (
                  <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                    <span className="text-sm text-yellow-700">Expiração do PIX:</span>
                    <div className="font-medium text-yellow-900">
                      {formatarDataHora(pixSelecionado.pix_expires_at)}
                    </div>
                  </div>
                )}

                {pixSelecionado.data_pagamento && (
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <span className="text-sm text-green-700">Data do Pagamento:</span>
                    <div className="font-medium text-green-900">
                      {formatarDataHora(pixSelecionado.data_pagamento)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* QR Code PIX */}
            {pixSelecionado.pix_qr_code && (
              <div>
                <div className="flex items-center gap-2 text-gray-900 font-semibold mb-3">
                  <QrCode className="w-5 h-5" />
                  QR Code PIX
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Código Pix Copia e Cola:</span>
                    <button
                      onClick={() => copiarTexto(pixSelecionado.pix_qr_code, 'Código PIX')}
                      className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                        copiado === 'Código PIX' ? 'text-green-600 bg-green-50' :
                        copiado === 'erro' ? 'text-red-600 bg-red-50' :
                        'text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <Copy className="w-4 h-4" />
                      {copiado === 'Código PIX' ? 'Copiado!' : copiado === 'erro' ? 'Erro!' : 'Copiar'}
                    </button>
                  </div>
                  <div className="bg-white p-3 rounded border border-gray-200 font-mono text-xs break-all">
                    {pixSelecionado.pix_qr_code}
                  </div>
                </div>

                {pixSelecionado.pix_qr_code_url && (
                  <div className="mt-4 text-center">
                    <img 
                      src={pixSelecionado.pix_qr_code_url} 
                      alt="QR Code PIX" 
                      className="mx-auto max-w-xs border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Links */}
            {pixSelecionado.checkout_url && (
              <div>
                <div className="flex items-center gap-2 text-gray-900 font-semibold mb-3">
                  <FileText className="w-5 h-5" />
                  Links de Pagamento
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">URL de Checkout:</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => copiarTexto(pixSelecionado.checkout_url, 'Link')}
                        className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                          copiado === 'Link' ? 'text-green-600 bg-green-50' :
                          copiado === 'erro' ? 'text-red-600 bg-red-50' :
                          'text-blue-600 hover:bg-blue-50'
                        }`}
                      >
                        <Copy className="w-4 h-4" />
                        {copiado === 'Link' ? 'Copiado!' : copiado === 'erro' ? 'Erro!' : 'Copiar'}
                      </button>
                      <div className="relative group">
                        <a
                          href={pixSelecionado.checkout_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-1 px-3 py-1 text-sm rounded-lg transition-colors ${
                            pixSelecionado.status === 'expired' || calcularHorasRestantes(pixSelecionado) <= 0
                              ? 'text-gray-400 hover:bg-gray-100 cursor-not-allowed'
                              : 'text-blue-600 hover:bg-blue-50'
                          }`}
                          title={pixSelecionado.status === 'expired' || calcularHorasRestantes(pixSelecionado) <= 0 ? "Link Pix Expirado. Crie novo Link" : "Abrir link de pagamento"}
                        >
                          <Eye className="w-4 h-4" />
                          Abrir
                        </a>
                        {pixSelecionado.status === 'expired' || calcularHorasRestantes(pixSelecionado) <= 0 ? (
                          <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded py-1 px-2 bottom-full left-1/2 transform -translate-x-1/2 mb-2 whitespace-nowrap">
                            Link Pix Expirado. Crie novo Link
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 bg-white p-2 rounded border border-gray-200 text-xs break-all">
                    {pixSelecionado.checkout_url}
                  </div>
                </div>
              </div>
            )}

            {/* Descrição e Observações */}
            {(pixSelecionado.descricao || pixSelecionado.observacoes) && (
              <div>
                <div className="text-gray-900 font-semibold mb-3">Informações Adicionais</div>
                
                {pixSelecionado.descricao && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-3">
                    <span className="text-sm text-gray-600">Descrição:</span>
                    <div className="text-gray-900 mt-1">{pixSelecionado.descricao}</div>
                  </div>
                )}

                {pixSelecionado.observacoes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <span className="text-sm text-gray-600">Observações:</span>
                    <div className="text-gray-900 mt-1">{pixSelecionado.observacoes}</div>
                  </div>
                )}
              </div>
            )}

            {/* Informações Técnicas */}
            <div>
              <div className="text-gray-900 font-semibold mb-3">Informações Técnicas</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm text-gray-600">Pedido ID:</span>
                  <div className="font-mono text-sm text-gray-900">{pixSelecionado.pedido_id}</div>
                </div>

                {pixSelecionado.codigo_pedido && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-sm text-gray-600">Código Pedido:</span>
                    <div className="font-mono text-sm text-gray-900">{pixSelecionado.codigo_pedido}</div>
                  </div>
                )}

                {pixSelecionado.criado_por && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <span className="text-sm text-gray-600">Criado por:</span>
                    <div className="text-sm text-gray-900">{pixSelecionado.criado_por}</div>
                  </div>
                )}

                <div className="bg-gray-50 rounded-lg p-3">
                  <span className="text-sm text-gray-600">Última Atualização:</span>
                  <div className="text-sm text-gray-900">
                    {formatarDataHora(pixSelecionado.updated_at)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
            <button
              onClick={fecharModalEAtualizar}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Fechar e Atualizar
            </button>
          </div>
        </div>
      </div>
  ) : null;

  // ⭐ NOVO: Modal de Reenviar PIX
  const ModalReenviar = () => {
    if (!modalReenviar || !pixParaReenviar) return null;

    const validarDados = () => {
      if (!dadosReenvio.celular || dadosReenvio.celular.trim() === '') {
        return { valido: false, erro: 'Celular é obrigatório para envio via WhatsApp' };
      }
      
      // Validação básica de celular (pelo menos 10 dígitos)
      const celularLimpo = dadosReenvio.celular.replace(/\D/g, '');
      if (celularLimpo.length < 10) {
        return { valido: false, erro: 'Celular deve ter pelo menos 10 dígitos' };
      }
      
      return { valido: true };
    };

    const handleEnviar = async () => {
      const validacao = validarDados();
      if (!validacao.valido) {
        alert(validacao.erro);
        return;
      }

      try {
        setReenviando(true);
        
        const response = await axios.post(`/api/pix/reenviar/${pixParaReenviar.id}`, {
          celular: dadosReenvio.celular,
          email: dadosReenvio.email || null
        });
        
        if (response.data.success) {
          setReenvioStatus({
            type: 'success',
            message: `PIX reenviado para ${pixParaReenviar.nome_cliente}`,
            detalhes: response.data.message
          });
          
          // Fechar modal e limpar dados
          setModalReenviar(false);
          setPixParaReenviar(null);
          setDadosReenvio({ celular: '', email: '' });
          
          // Atualizar lista após 2 segundos
          setTimeout(() => {
            carregarPixList();
          }, 2000);
          
          // Limpar status após 5 segundos
          setTimeout(() => {
            setReenvioStatus(null);
          }, 5000);
        } else {
          throw new Error(response.data.error || 'Erro ao reenviar PIX');
        }
      } catch (error) {
        console.error('❌ Erro ao reenviar PIX:', error);
        
        const mensagemErro = error.response?.data?.error || 
                            error.response?.data?.message || 
                            error.message || 
                            'Erro ao reenviar PIX';
        
        const detalhesErro = error.response?.data?.detalhes || 
                            error.response?.data?.recomendacao || 
                            'Tente novamente mais tarde';
        
        setReenvioStatus({
          type: 'error',
          message: mensagemErro,
          detalhes: detalhesErro
        });
        
        // Limpar status após 8 segundos para erros
        setTimeout(() => {
          setReenvioStatus(null);
        }, 8000);
      } finally {
        setReenviando(false);
      }
    };

    const handleCancelar = () => {
      setModalReenviar(false);
      setPixParaReenviar(null);
      setDadosReenvio({ celular: '', email: '' });
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Reenviar PIX</h3>
              <p className="text-sm text-gray-500">Cliente: {pixParaReenviar.nome_cliente}</p>
              <p className="text-sm text-gray-500">Valor: {formatarValor(pixParaReenviar.valor)}</p>
            </div>
            <button
              onClick={handleCancelar}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="p-6 space-y-6">
            {/* Informações do PIX */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Informações do PIX</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Status:</span>
                  <StatusBadge status={pixParaReenviar.status} />
                </div>
                <div>
                  <span className="text-gray-600">Expira em:</span>
                  <div className="font-medium text-gray-900">{formatarTempoRestante(pixParaReenviar)}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-600">Código Cliente:</span>
                  <div className="font-medium text-gray-900">{pixParaReenviar.codigo_cliente}</div>
                </div>
              </div>
            </div>

            {/* Formulário de Contato */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">Dados para Reenvio</h4>
              
              {/* Celular */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Celular <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={dadosReenvio.celular}
                  onChange={(e) => setDadosReenvio(prev => ({ ...prev, celular: e.target.value }))}
                  placeholder="(11) 99999-9999"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Obrigatório para envio via WhatsApp. Use DDD + número.
                </p>
              </div>

              {/* Email */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email (opcional)
                </label>
                <input
                  type="email"
                  value={dadosReenvio.email}
                  onChange={(e) => setDadosReenvio(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="cliente@email.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Para envio de email com o código PIX (opcional).
                </p>
              </div>

              {/* Dica */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Atenção</p>
                    <p className="text-xs text-blue-700">
                      O PIX será reenviado apenas se tiver mais de 6 horas para expirar.
                      Caso contrário, será necessário criar um novo link.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
            <button
              onClick={handleCancelar}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={reenviando}
            >
              Cancelar
            </button>
            <button
              onClick={handleEnviar}
              disabled={reenviando}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {reenviando ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enviando...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />
                  Reenviar PIX
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading && pixList.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando PIX...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">PIX Emitidos</h2>
          <p className="text-gray-600 mt-1">
            Visualize todos os links de pagamento PIX gerados
            {filtros.orderDirection === 'DESC' && ' (mais recentes primeiro)'}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              mostrarFiltros 
                ? 'bg-blue-50 border-blue-300 text-blue-700' 
                : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            Filtros
            {(filtros.search || filtros.status || filtros.dataInicio || filtros.dataFim) && (
              <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                Ativos
              </span>
            )}
          </button>
          <button
            onClick={carregarPixList}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-medium mb-1">Total de Links</h3>
            <p className="text-3xl font-bold text-gray-900">{estatisticas.total_links || 0}</p>
            <p className="text-sm text-gray-500 mt-2">
              {formatarValor(estatisticas.valor_total_gerado)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
            </div>
            <h3 className="text-gray-700 text-sm font-medium mb-1">Pagos</h3>
            <p className="text-3xl font-bold text-green-900">{estatisticas.total_pagos || 0}</p>
            <p className="text-sm text-green-700 mt-2">
              {formatarValor(estatisticas.valor_total_pago)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 border border-yellow-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-yellow-200 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-700" />
              </div>
            </div>
            <h3 className="text-gray-700 text-sm font-medium mb-1">Pendentes</h3>
            <p className="text-3xl font-bold text-yellow-900">{estatisticas.total_pendentes || 0}</p>
            <p className="text-sm text-yellow-700 mt-2">
              {formatarValor(estatisticas.valor_total_pendente)}
            </p>
          </div>

          <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6 border border-red-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-200 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-700" />
              </div>
            </div>
            <h3 className="text-gray-700 text-sm font-medium mb-1">Expirados</h3>
            <p className="text-3xl font-bold text-red-900">{estatisticas.total_expirados || 0}</p>
          </div>
        </div>
      )}

      {/* ⭐ ATUALIZADO: Painel de Filtros */}
      {mostrarFiltros && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filtros de Pesquisa</h3>
            <button
              onClick={limparFiltros}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Limpar tudo
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Busca por Nome/Código */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="w-4 h-4 inline mr-1" />
                Buscar por Nome ou Código
              </label>
              <input
                type="text"
                value={filtros.search}
                onChange={(e) => setFiltros(prev => ({ ...prev, search: e.target.value }))}
                placeholder="Digite o nome ou código do cliente..."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onKeyPress={(e) => e.key === 'Enter' && aplicarFiltros()}
              />
            </div>

            {/* Filtro de Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status do Pagamento
              </label>
              <select
                value={filtros.status}
                onChange={(e) => setFiltros(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos os Status</option>
                <option value="paid">✅ Pago</option>
                <option value="pending">⏳ Pendente</option>
                <option value="expired">❌ Expirado</option>
                <option value="canceled">🚫 Cancelado</option>
              </select>
            </div>

            {/* Ordenação */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ordenar por
              </label>
              <select
                value={filtros.orderBy}
                onChange={(e) => setFiltros(prev => ({ ...prev, orderBy: e.target.value, page: 1 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="created_at">Data de Criação</option>
                <option value="valor">Valor</option>
                <option value="nome_cliente">Nome do Cliente</option>
                <option value="data_pagamento">Data de Pagamento</option>
              </select>
            </div>

            {/* Data Início */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data Início
              </label>
              <input
                type="date"
                value={filtros.dataInicio}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Data Fim */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data Fim
              </label>
              <input
                type="date"
                value={filtros.dataFim}
                onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Botão Aplicar Filtros */}
            <div className="lg:col-span-2 flex items-end">
              <button
                onClick={aplicarFiltros}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Search className="w-4 h-4 inline mr-2" />
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status de Reenvio */}
      {reenvioStatus && (
        <div className={`rounded-xl p-4 flex items-start gap-3 border ${
          reenvioStatus.type === 'success' 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          {reenvioStatus.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h4 className={`font-medium ${
              reenvioStatus.type === 'success' ? 'text-green-900' : 'text-red-900'
            }`}>
              {reenvioStatus.message}
            </h4>
            {reenvioStatus.detalhes && (
              <p className={`text-sm mt-1 ${
                reenvioStatus.type === 'success' ? 'text-green-700' : 'text-red-700'
              }`}>
                {reenvioStatus.detalhes}
              </p>
            )}
          </div>
          <button
            onClick={() => setReenvioStatus(null)}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900">Erro ao carregar dados</h4>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* ⭐ ATUALIZADO: Tabela com Cabeçalhos Ordenáveis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <HeaderOrdenavel campo="created_at">
                  Data Emissão
                </HeaderOrdenavel>
                <HeaderOrdenavel campo="nome_cliente">
                  Cliente
                </HeaderOrdenavel>
                <HeaderOrdenavel campo="valor">
                  Valor PIX
                </HeaderOrdenavel>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Venc. Parcela
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <HeaderOrdenavel campo="data_pagamento">
                  Data Pagamento
                </HeaderOrdenavel>
                <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : pixList.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="text-gray-500">
                      <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="font-medium">Nenhum PIX encontrado</p>
                      <p className="text-sm mt-1">
                        {filtros.search || filtros.status || filtros.dataInicio || filtros.dataFim
                          ? 'Tente ajustar os filtros de busca'
                          : 'Nenhum link PIX foi gerado ainda'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                pixList.map((pix) => {
                  return (
                    <tr key={pix.id} className="hover:bg-gray-50 transition-colors">  
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">
                            {formatarData(pix.created_at)}
                          </div>
                          <div className="text-gray-500">
                            {formatarHora(pix.created_at)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{pix.nome_cliente}</div>
                          <div className="text-gray-500">Cód: {pix.codigo_cliente}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-blue-600">
                          {formatarValor(pix.valor)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatarData(pix.data_vencimento_original)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={pix.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {pix.data_pagamento ? (
                          <div className="text-sm">
                            <div className="font-medium text-green-600">
                              {formatarData(pix.data_pagamento)}
                            </div>
                            <div className="text-gray-500">
                              {formatarHora(pix.data_pagamento)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => carregarDetalhes(pix.id)}
                          disabled={loadingDetalhes}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Ver detalhes completos do PIX"
                        >
                          <Eye className="w-4 h-4" />
                          Detalhes
                        </button>

                        <button
                          onClick={() => podeReenviarPix(pix) && abrirModalReenviar(pix)}
                          disabled={!podeReenviarPix(pix) || reenviando}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            podeReenviarPix(pix)
                              ? 'text-green-600 hover:bg-green-50 cursor-pointer'
                              : 'text-gray-400 bg-gray-100 cursor-not-allowed opacity-60'
                          }`}
                          title={
                            podeReenviarPix(pix)
                              ? `Reenviar PIX para ${pix.nome_cliente}`
                              : pix.status !== 'pending'
                                ? 'PIX já pago, expirado ou cancelado'
                                : !pix.celular
                                  ? 'Sem celular cadastrado'
                                  : 'Menos de 6h para expirar — crie um novo link'
                          }
                        >
                          <Send className="w-4 h-4" />
                          Reenviar PIX
                        </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}  
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              Página {pagination.page} de {pagination.totalPages}
              <span className="ml-2 text-gray-500">({pagination.total} registros)</span>
            </div>
            <div className="flex gap-2">
              <button
                disabled={!pagination.hasPrev}
                onClick={() => setFiltros(prev => ({ ...prev, page: prev.page - 1 }))}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                disabled={!pagination.hasNext}
                onClick={() => setFiltros(prev => ({ ...prev, page: prev.page + 1 }))}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-lg hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes */}
      {modalDetalhesNode}
    </div>
  );
};

export default ListaPixEmitidos;